import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import _ from "lodash";
import cx from "classnames";
import dateFormat from "dateformat";

import { FactCheckEvaluation, LearningRecordSimulation, SimulationAssessmentReport, SimulationParameterEvaluation } from "src/types/coaching/learners";


import { Persona } from "src/types/coaching/simulation";
import Actions from "src/components/actionbar";
import { useTransition, animated, useSpring, useSpringRef } from "@react-spring/web";
import { useSelector } from "react-redux";
import { getFullName } from "src/utils/utils";
import Thumbnail from "src/components/thumbnail";

import DOMPurify from "dompurify";

import styles from './simulator.module.scss';
import { Media } from "src/types/media";
import { useVapi } from "./hook";
import Slider from "src/components/slider";
import Button from "src/components/button";
import { Nav, Tab } from "react-bootstrap";
import { CALL_TYPES, DIFFICULTY_CHOICES } from "src/constants/simulation";
import { SimulationContext } from "../context";
import { FileService } from "src/utils/file.service";
import { SoundVisualizer } from "./visualiser";
import ScrollToBottom from 'react-scroll-to-bottom';
import { CallRecordingPlayer } from "./player";
import { DndContext, PointerSensor, useDndMonitor, useDraggable, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import { CSS } from '@dnd-kit/utilities';

interface CallSimulatorProps {
  simulation: LearningRecordSimulation;
  className?: string;
}

export const CallSimulator: React.FC<CallSimulatorProps> = ({ simulation,className }) => {
  const simulationContext = React.useContext(SimulationContext);

  const name = useMemo(() => _.get(simulation, "simulation.name") || "", [simulation]);

  const { vapiClient, started, ended } = useVapi();
  const [showCompleteConfirmation, setShowCompleteConfirmation] = React.useState(false);

  const [callInitialised, setCallInitialised] = React.useState(false);
  const [showDetails, setShowDetails] = React.useState(false);
  const [showTranscription, setShowTranscription] = React.useState(false);

  const showCall = simulationContext.view === "simulator";
  const showCompleted = simulationContext.view === "completion";
  const showFailed = simulationContext.view === "failed";
  const showAssessmentView = simulationContext.view === 'report';

  const componentWillUnmount = useRef(false)

  const callTransition = useTransition(showCall, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const sideBarTransition = useTransition(showDetails || showTranscription, {
    from: { opacity: 0, transform: "translateX(100%)" },
    enter: { opacity: 1, transform: "translateX(0%)" },
    leave: { opacity: 0, transform: "translateX(100%)" },
  });

  const completeTransition = useTransition(showCompleted, {
    from: { opacity: 1, transform: "translateY(0px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(100px)" },
  });

  const failedTransition = useTransition(showFailed, {
    from: { opacity: 1, transform: "translateY(0px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(100px)" },
  });

  const assessmentTransition = useTransition(showAssessmentView, {
    from: { opacity: 1, transform: "translateY(0px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(100px)" },
  });

  const handleDetailsClicked = () => {
    setShowDetails(!showDetails);
    setShowTranscription(false);
  }

  const handleTranscriptionClicked = () => {
    setShowTranscription(!showTranscription);
    setShowDetails(false);
  }

  const handleExit = () => {
    if (simulationContext.view === 'report') {
      if (simulationContext.secondaryView === 'call-recording') {
        simulationContext.changeSecondaryView?.(null);
      } else {
        simulationContext.changeView?.("summary");
      }

    } else if (simulationContext.view === 'simulator') {
      setShowCompleteConfirmation(true);
    } else if (simulationContext.view === 'completion') {
      simulationContext.changeView?.("summary");
    } else if (simulationContext.view === 'failed') {
      simulationContext.changeView?.("summary");
    }
  }

  const handleEndSimulation = (confirm) => {
    setShowTranscription(false);
    setShowDetails(false);

    if (simulationContext.view === 'simulator') {
      if (confirm) {
        setShowCompleteConfirmation(true);
      } else {
        handleEndSimulationConfirmation();
      }
    }
  }

  const handleEndSimulationConfirmation = () => {
    vapiClient?.stop();
    
    if (!callInitialised) {
      // Call not connected, manually end
      setShowCompleteConfirmation(false);
      simulationContext.endSimulation?.(simulation); 
    }
  }

  const handleAssessmentClicked = () => {
    if (simulationContext.viewReport) {
      simulationContext.viewReport();
    }
  }

  const handleRestartSimulation = () => {
    if (simulationContext.restartSimulation) {
      simulationContext.restartSimulation();
    }

    setCallInitialised(false);
  }

  const handleCallEnd = React.useCallback(() => {
    setShowCompleteConfirmation(false);
    const inProgress = simulation?.status === "IN_PROGRESS";
    if (inProgress && vapiClient) {
      vapiClient.stop();
      simulationContext.endSimulation?.(simulation);
    }
  }, [simulation, vapiClient]);

  const handleViewCallRecording = () => {
    simulationContext.changeSecondaryView?.("call-recording");
  }

  useEffect(() => {
    return () => {
      // Cleanup function to handle component unmount
      componentWillUnmount.current = true;
    }
  }, []);

  useEffect(() => {
    const inProgress = simulation?.status === "IN_PROGRESS";
    if (vapiClient && inProgress && !started && !callInitialised && simulation?.session) {
      console.log("Starting call for session " + simulation.session);
      vapiClient.start(simulation.session);
    }

    return () => {
      if (componentWillUnmount.current && vapiClient && inProgress && started && !ended) {
        console.log("Ending call " + simulation?.session);
        handleCallEnd();
      }
    }
  }, [vapiClient, simulation?.session, started, callInitialised]);

  useEffect(() => {
    if (started) {
      setCallInitialised(true);
    }
  }, [started]);

  useEffect(() => {
    if (ended) {
      handleCallEnd();
    }
  }, [ended]);

  return (
    <div
      className={cx(
        styles.callSimulationContainer,
        {
          [styles.open]: showDetails || showTranscription,
        },
        className
      )}
    >
      <div className={styles.mainContainer}>
        <div className={styles.header}>
          <img className={styles.logo} src="/images/coaching/logo.svg" alt="Coaching" />
          <div className={styles.divider} />
          <div className={styles.name}>{name}</div>
          <div className={styles.actions}>
            {showAssessmentView && simulationContext.secondaryView !== 'call-recording' &&
              <>
                <Button className={styles.viewRecordingAction} variant="secondary" icon="paperflite-waveform" text="view call recording" onClick={handleViewCallRecording} />
                <div className={styles.divider} />
              </>
            }

            <button className={styles.action} onClick={handleExit}>
              <i className="paperflite-close" />
            </button>
          </div>
        </div>
        <div className={styles.body}>
          {callTransition(
            (style, item) =>
              item && (
                <animated.div style={style} className={styles.stack}>
                  <CallContainer
                    simulation={simulation}
                    started={callInitialised}
                    onDetailsClicked={handleDetailsClicked}
                    onTranscriptionClicked={handleTranscriptionClicked}
                    onEndSimulation={handleEndSimulation}
                  />
                </animated.div>
              )
          )}

          {completeTransition(
            (style, item) =>
              item && (
                <animated.div style={style} className={styles.stack}>
                  <CompletedCallView simulation={simulation}
                    onFeedback={handleAssessmentClicked}
                    onExit={handleExit}
                  />
                </animated.div>
              )
          )}

          {failedTransition(
            (style, item) =>
              item && (
                <animated.div style={style} className={styles.stack}>
                  <FailedCallView simulation={simulation}
                    report={simulationContext.assessmentReport!}
                    onRestart={handleRestartSimulation}
                    onExit={handleExit}
                  />
                </animated.div>
              )
          )}

          {assessmentTransition(
            (style, item) => {
              if (item) {

                const { transform, ...rest } = style;

                return (
                  <animated.div style={{
                    ...rest,
                    transform: transform.to((t) => (t ? `translateX(${t}%)` : 'none')),
                  }} className={styles.stack}>
                    {simulationContext.assessmentReport &&
                      <CallAssessmentView simulation={simulation} report={simulationContext.assessmentReport!} />
                    }
                    {!simulationContext.assessmentReport &&
                      <div className={styles.loading}>Loading assessment report...</div>
                    }
                  </animated.div>
                )
              }
            })}
        </div>
      </div>
      {sideBarTransition(
        (style, item) =>
          item && (
            <animated.div style={style} className={styles.sideContainer}>
              {showTranscription ? <TranscriptionViewer simulation={simulation} onClose={() => setShowTranscription(false)} /> : null}
              {showDetails ? <SimulationDetails simulation={simulation} onClose={() => setShowDetails(false)} /> : null}
            </animated.div>
          )
      )}

      <CompleteCallDialog
        show={showCompleteConfirmation}
        simulation={simulation}
        onFinish={handleEndSimulationConfirmation}
        onClose={() => {
          setShowCompleteConfirmation(false);
        }}
      />
    </div >
  );
}
interface CallContainerProps {
  simulation: LearningRecordSimulation;
  started: boolean;
  onDetailsClicked: () => void;
  onTranscriptionClicked: () => void;

  // eslint-disable-next-line no-unused-vars
  onEndSimulation: (confirm: boolean) => void;
}

const CallContainer: React.FC<CallContainerProps> = ({ simulation, started, onDetailsClicked, onTranscriptionClicked, onEndSimulation }) => {
  const simulationContext = React.useContext(SimulationContext);
  const endingSimulation = simulationContext.endingSimulation || false;

  const menuTransitions = useTransition(started, {
    initial: { opacity: 0, transform: "translateY(100%)" },
    from: { opacity: 0, transform: "translateY(100%)" },
    enter: { opacity: 1, transform: "translateY(0%)" },
    leave: { opacity: 0, transform: "translateY(-100%)" },
  });

  const disclaimerTransitions = useTransition(!started, {
    initial: { opacity: 0, transform: "translateY(100%)" },
    from: { opacity: 0, transform: "translateY(100%)" },
    enter: { opacity: 1, transform: "translateY(0%)" },
    leave: { opacity: 0, transform: "translateY(-100%)" },
  });

  return (
    <div className={cx(styles.callContainer, {
      [styles.loading]: !started,
    })}>
      <div className={styles.participants}>
        <ProspectParticipantView persona={simulation.simulation!.persona!} loading={!started} />
        <UserParticipantView />
      </div>

      {menuTransitions((style, item) => (
        item &&
        <animated.div style={style} className={styles.callMenuBar}>
          <Actions.Row>
            <Actions.Item className={cx(styles.menuItem, styles.timer)}>
              <Timer duration={simulation.simulation!.duration! * 60} disabled={endingSimulation} onFinish={() => onEndSimulation(false)} />
            </Actions.Item>
            <Actions.IconButton disabled={endingSimulation} className={styles.menuItem} icon="paperflite-info" onClick={onDetailsClicked} />
            <Actions.IconButton disabled={endingSimulation} className={styles.menuItem} icon="paperflite-document" onClick={onTranscriptionClicked} />
            <Actions.Button disabled={endingSimulation} className={cx(styles.menuItem, styles.endButton)} variant="coaching-primary"
              icon="fa-solid fa-phone"
              title={endingSimulation ? "ending call..." : "end"}
              onClick={() => { onEndSimulation(true) }}
            />
          </Actions.Row>
        </animated.div>
      ))}

      {disclaimerTransitions((style, item) => (
        item &&
        <animated.div className={styles.disclaimer} style={style}>
          <i className={cx(styles.icon, 'paperflite-info')}></i>
          <p>We&apos;ll need more than two minutes to conduct a proper assessment</p>
        </animated.div>
      ))}

    </div>
  );
}

interface ProspectParticipantViewProps {
  persona: Persona;
  loading: boolean;
}

const ProspectParticipantView: React.FC<ProspectParticipantViewProps> = ({ loading, persona }) => {
  const { started, volume } = useVapi();

  return (
    <div className={cx(styles.participantContainer, styles.prospect, {
      [styles.loading]: loading,
    })}>
      <div className={styles.participant}>
        <div className={styles.avatar}>
          <Thumbnail className={styles.icon} media={persona.icon} defaultImage="/images/assets/simulationThumbnail.jpg" />
          {loading &&
            <div className={styles.loader}>
              <div className={styles.orbit1}>
                <i className={cx("paperflite-certificate", styles.planet)} />
                <i className={cx("paperflite-volume-on", styles.planet)} />
              </div>
              <div className={styles.orbit2}>
                <i className={cx("paperflite-text-message", styles.planet)} />
                <i className={cx("paperflite-mic", styles.planet)} />
              </div>
            </div>
          }
        </div>
        <div className={styles.name}>{persona.name}</div>
        {loading && <ProgressIndicator />}
      </div>
      <div className={styles.voiceVisualizer}>
        <SoundVisualizer
          disabled={!started}
          volumeLevel={volume}
          barCount={36}
          barWidth="2px"
          cornerRadius={8}
          height={36}
          color="#E5E5E5"
          durationInSeconds={0.25}
          verticalAlignment="center"
        />
      </div>
    </div>
  );
}

const ProgressIndicator: React.FC = () => {
  const steps = [
    'Checking their microphone...',
    "Establishing a stable connection...",
    "Optimizing their audio quality...",
    "Scanning for background noise...",
    "Almost there — hang tight!",
  ];

  const [step, setStep] = useState('Setting up mic and speakers');

  useEffect(() => {
    const interval = setInterval(() => {
      setStep((prev) => {
        const index = steps.indexOf(prev);
        if (index === -1 || index === steps.length - 1) {
          return steps[0];
        }
        return steps[index + 1];
      });
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className={styles.progressIndicator}>
      <div className={styles.step}>
        {step}
      </div>
      <div className={styles.progress}>
        <div className={styles.bar} />
      </div>
    </div>
  )
}

const UserParticipantView = () => {

  const { vapiClient, started } = useVapi();
  const [volumeLevel, setVolumeLevel] = useState(0);

  const { name, icon } = useSelector((state: any) => {
    const userProfile = _.get(state, "users.me");
    return {
      name: getFullName(userProfile),
      icon: _.get(userProfile, "icon"),
    }
  }, _.isEqual);

  useEffect(() => {
    if (vapiClient && started) {
      const call = vapiClient.getDailyCallObject();

      if (call) {
        call.startLocalAudioLevelObserver(100);

        call.on('local-audio-level', (event) => {
          const audioLevel = event.audioLevel;

          const volumeLevel = Math.min(1, audioLevel / 0.15);
          setVolumeLevel(volumeLevel);
        });
      }
    }

    return () => {
      if (vapiClient) {
        const call = vapiClient.getDailyCallObject();
        if (call) {
          call.stopLocalAudioLevelObserver();
        }
      }
    }
  }, [vapiClient, started]);

  return (
    <div className={cx(styles.participantContainer, styles.user)}>
      <div className={styles.participant}>
        <div className={styles.avatar}>
          <Thumbnail className={styles.icon} media={icon} defaultImage="/images/assets/default_asset.jpg" />
        </div>
        <div className={styles.name}>{name}</div>
      </div>
      <div className={styles.voiceVisualizer}>
        <SoundVisualizer
          disabled={!started}
          volumeLevel={volumeLevel}
          barCount={36}
          barWidth="2px"
          cornerRadius={8}
          height={36}
          color="#E5E5E5"
          durationInSeconds={0.25}
          verticalAlignment="center"
        />
      </div>
    </div>
  );
}


interface SimulationDetailsProps {
  simulation: LearningRecordSimulation;
  onClose: () => void;
}

const SimulationDetails: React.FC<SimulationDetailsProps> = ({ simulation, onClose }) => {

  const callType = useMemo(() => {
    const value = _.get(simulation, "simulation.type") || "";
    return CALL_TYPES.find((item) => item.id === value)?.title || "";
  }, [simulation]);

  const difficulty = useMemo(() => {
    const value = _.get(simulation, "simulation.difficulty") || "";
    return DIFFICULTY_CHOICES.find((item) => item.value === value)?.label || "";
  }, [simulation]);

  return (
    <div className={styles.simulationDetailsContainer}>
      <header>
        <h4>Call Info</h4>
        <button className={styles.closeButton} onClick={onClose}>
          <i className="paperflite-right-arrow" />
        </button>
      </header>
      <div className={cx(styles.callInfo)}>
        <div className={styles.name}>{simulation.simulation.name || ""}</div>
        <div className={styles.description}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(simulation.simulation.description || ""),
          }}
        />
        <div className={styles.metadatas}>
          <div className={styles.metadata}>
            <i className="paperflite-clock"></i>
            <span>{simulation.simulation.duration} minutes</span>
          </div>
          <div className={styles.border} />
          <div className={styles.metadata}>
            <i className="paperflite-calendar"></i>
            <span>{callType}</span>
          </div>
          <div className={styles.border} />
          <div className={styles.metadata}>
            <i className="paperflite-speedomenter"></i>
            <span>{difficulty}</span>
          </div>
        </div>
      </div>
      <div className={styles.property}>
        <h6 className={styles.name}>Organization Info</h6>
        { simulation.simulation?.organization && simulation.simulation?.organization?.description &&
          <div className={styles.body}>
            <div className={styles.organization}>
              <div className={styles.name}>
                {simulation.simulation?.organization?.name || ""}
              </div>
              <div className={styles.description}>
                {simulation.simulation?.organization?.description || ""}
              </div>
            </div>
          </div>
          }
          { simulation.simulation?.organization && !simulation.simulation?.organization.description &&
            <div className={styles.body}>
            <div className={styles.organization}>
              <div className={styles.description}>
                No information
              </div>
            </div>
          </div>
          }
      </div>
      <div className={styles.property}>
        <h6 className={styles.name}>Persona</h6>
        <div className={styles.body}>
          <div className={styles.persona}>
            <div className={styles.name}>
              {simulation.simulation?.persona?.name || ""}
            </div>
            <div className={styles.description}>
              {simulation.simulation?.persona?.description || ""}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.property}>
        <h6 className={styles.name}>Objectives</h6>
        <div className={styles.body}>
          <div className={styles.objectives}>
            <div className={styles.description}>
              {simulation.simulation?.objectives?.description || ""}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface TranscriptionViewerProps {
  simulation: LearningRecordSimulation;
  onClose: () => void;
}

const TranscriptionViewer: React.FC<TranscriptionViewerProps> = ({ simulation, onClose }) => {
  const { transcripts } = useVapi();
  const userProfile = useSelector((state: any) => {
    const profile = _.get(state, "users.me");
    return {
      name: getFullName(profile),
      icon: _.get(profile, "icon"),
    }
  }, _.isEqual);

  const personaProfile = useMemo(() => {
    const persona = simulation.simulation?.persona;
    return {
      name: persona?.name || "",
      icon: persona?.icon,
    }
  }, [simulation]);


  return (
    <ScrollToBottom className={cx(styles.transcriptionContainer)} scrollViewClassName={styles.scrollView} followButtonClassName={styles.scrollButton}>
      <header>
        <h4>Transcript</h4>
        <button className={styles.closeButton} onClick={onClose}>
          <i className="paperflite-right-arrow" />
        </button>
      </header>

      <div className={styles.transcripts}>
        {(transcripts || []).map((transcript, index) => {
          const role = transcript.role;
          const speaker = role === "user" ? userProfile : personaProfile;
          return (
            <TranscriptRow
              key={index}
              speaker={speaker}
              text={transcript.content}
              time={""}
            />
          )
        }
        )}
      </div>
    </ScrollToBottom>
  )
}

interface TranscriptRowProps {
  speaker: {
    name: string;
    icon?: Media;
  };
  text: string;
  time: string | boolean;
}

const TranscriptRow: React.FC<TranscriptRowProps> = ({ speaker, text, time }) => {
  return (
    <div className={styles.transcriptRow}>
      {time && <div className={styles.time}>{time}</div>}
      <div className={styles.speaker}>
        <Thumbnail className={styles.icon} media={speaker.icon} defaultImage="/images/assets/simulationThumbnail.jpg" />
        <div className={styles.name}>{speaker.name}</div>
      </div>
      <div className={styles.text}>{text}</div>
    </div>
  )
}

const TranscriptRowPlaceholder: React.FC = () => {
  return (
    <div className={cx(styles.transcriptRow, styles.placeholder)}>
      <div className={styles.speaker}>
        <div className={cx(styles.icon, styles.block)} />
        <div className={cx(styles.name, styles.block)}></div>
      </div>
      <div className={cx(styles.text)}>
        <div className={cx(styles.block)}></div>
        <div className={cx(styles.block)}></div>
        <div className={cx(styles.block)}></div>
      </div>
    </div>
  );
}

// End call 
interface CompleteCallDialogProps {
  show: boolean;
  simulation: LearningRecordSimulation;
  onFinish: () => void;
  onClose: () => void;
}

const CompleteCallDialog: React.FC<CompleteCallDialogProps> = ({ show, simulation, onFinish, onClose }) => {
  return (
    <Slider.Dialog show={show} width="380px" onClose={onClose} className={styles.completeCallDialog}>
      <Slider.Header title="ending call" message={simulation.simulation.name} />
      <Slider.Body>
        <div className={styles.completeCallContainer}>
          <p>Are you sure you would like to end this call?</p>
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button className={styles.actionButton} variant="primary" icon="paperflite-tick" text="Confirm" onClick={onFinish} />
        <Button className={styles.actionButton} variant="secondary" text="Cancel" onClick={onClose} />
      </Slider.Actions>
    </Slider.Dialog>
  )
}

//Completed call screen
interface CompletedCallViewProps {
  simulation: LearningRecordSimulation;
  onFeedback: () => void;
  onExit: () => void;
}

const CompletedCallView: React.FC<CompletedCallViewProps> = ({ simulation, onFeedback, onExit }) => {
  const simulationContext = React.useContext(SimulationContext);
  const [animate, setAnimate] = React.useState(false);

  const duration = useMemo(() => {
    const duration = simulation.duration || 0;
    return FileService.formatDuration(duration, "long");
  }, [simulation]);

  const evaluationStatus = useMemo(() => {
    return simulation.report?.status || "";
  }, [simulation]);

  const personaRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  const personaAnimationRef = useSpringRef();
  const personaAnimation = useSpring({
    ref: personaAnimationRef,
    transform: `translate3d(0px, 0px, 0px) scale(1)`,
  });

  const userAnimationRef = useSpringRef();
  const userAnimation = useSpring({
    ref: userAnimationRef,
    transform: `translate3d(0px, 0px, 0px) scale(1)`,
  });

  useEffect(() => {
    if (personaRef.current && userRef.current && !animate) {
      setTimeout(() => {
        setAnimate(true);
      }, 1000);
    }
  }, [animate, personaRef.current, userRef.current]);

  useEffect(() => {
    if (animate) {
      const pAvatar = personaRef.current?.querySelector(`.${styles.avatar}`);
      const userAvatar = userRef.current?.querySelector(`.${styles.avatar}`);
      const pLeft = pAvatar?.getBoundingClientRect().left || 0;
      const uLeft = userAvatar?.getBoundingClientRect().left || 0;
      const width = pAvatar?.getBoundingClientRect().width || 0;
      const scale = 0.57;

      const distanceBetween = Math.abs(uLeft - pLeft - width) / 0.72;
      let distance = ((distanceBetween - 24) / 2);
      distance = Math.max(distance, 12);

      personaAnimationRef.start({
        transform: `translate3d(${distance}px, 21px, 0px) scale(${scale})`,
      });

      userAnimationRef.start({
        transform: `translate3d(-${distance}px, -21px, 0px) scale(${scale}`,
      });
    }
  }, [animate]);

  return (
    <div className={cx(styles.callContainer, styles.completed)}>
      <div className={styles.participants}>
        <animated.div ref={personaRef} style={personaAnimation}>
          <ProspectParticipantView persona={simulation.simulation!.persona!} loading={false} />
        </animated.div>
        <animated.div ref={userRef} style={userAnimation}>
          <UserParticipantView />
        </animated.div>
      </div>
      <div className={styles.details}>
        <h4 className={styles.message}>Your call has been ended!</h4>
        <div className={styles.duration}>duration: {duration}</div>
        <div className={styles.actions}>
          {evaluationStatus === "completed" &&
            <Button className={styles.actionButton} variant="primary"
              icon="paperflite-right-arrow"
              text={simulationContext.fetchingReport ? "Fetching feedback..." : "View feedback"}
              disabled={simulationContext.fetchingReport}
              onClick={onFeedback}
            />
          }
          {evaluationStatus !== "completed" &&
            <Button className={styles.actionButton} variant="primary"
              icon="paperflite-analytics"
              text={"Processing feedback..."}
            />
          }
          <Button className={styles.actionButton}
            variant="secondary"
            icon="paperflite-close"
            disabled={simulationContext.fetchingReport}
            text="Exit" onClick={onExit}
          />
        </div>
      </div>
    </div>
  )
}

//Call Failed
interface FailedCallViewProps {
  simulation: LearningRecordSimulation;
  report: SimulationAssessmentReport;
  onRestart: () => void;
  onExit: () => void;
}

const FailedCallView: React.FC<FailedCallViewProps> = ({ simulation, report, onRestart, onExit }) => {
  const simulationContext = React.useContext(SimulationContext);

  return (
    <div className={cx(styles.failedCallContainer)}>
      <div className={styles.wrapper}>
        <img className={styles.icon} src="/images/coaching/hour_glass.svg" alt="Failed" />
        <h4 className={styles.message}>You need at-least 2+ minutes of call time to get score and feedback analysis</h4>
        <div className={styles.details}>
          <div className={styles.metadata}>
            <div className={styles.label}>
              <i className="paperflite-calendar"></i>
              Call Logged
            </div>
            <div className={styles.value}>
              {dateFormat(report?.startedDate, 'dd mmm yyyy hh:MMTT')}
            </div>
          </div>
          <div className={styles.divider} />
          <div className={styles.metadata}>
            <div className={styles.label}>
              <i className="paperflite-clock"></i>
              Duration
            </div>
            <div className={styles.value}>{FileService.formatDuration(report?.duration, 'long')}</div>
          </div>
          <div className={styles.divider} />
          <div className={cx(styles.metadata, styles.persona)}>
            <div className={styles.label}>
              <i className="paperflite-user-settings"></i>
              Prospect
            </div>
            <div className={styles.value}>
              <Thumbnail className={styles.icon} media={null} defaultImage="/images/assets/default_asset.jpg" />
              {simulation.simulation?.persona?.name || ""}, {simulation.simulation?.persona?.designation || ""}
            </div>
          </div>
        </div>
        <div className={styles.actions}>
          <Button className={styles.actionButton} variant="primary"
            icon="paperflite-reverse"
            text={simulationContext.restartingSimulation ? "Restarting..." : "Try again"}
            disabled={simulationContext.restartingSimulation}
            onClick={onRestart}
          />
          <Button className={styles.actionButton}
            variant="secondary"
            icon="paperflite-close"
            disabled={simulationContext.restartingSimulation}
            text="Exit" onClick={onExit}
          />
        </div>
      </div>
    </div>
  )
}
interface CallAssessmentViewProps {
  simulation: LearningRecordSimulation;
  report: SimulationAssessmentReport;
}

const CallAssessmentView: React.FC<CallAssessmentViewProps> = ({ simulation, report }) => {
  const simulationContext = React.useContext(SimulationContext);

  const playerMinimized = simulationContext.secondaryView === 'call-recording-minimized';
  const showAssessmentView = simulationContext.secondaryView === null || playerMinimized;
  const showCallRecordingView = simulationContext.secondaryView === 'call-recording' || playerMinimized;

  const assessmentTransition = useTransition(showAssessmentView, {
    from: { opacity: 1, transform: "translateY(0px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(100px)" },
  });

  const callRecordingTransition = useTransition(showCallRecordingView, {
    from: { opacity: 1, transform: "translateY(0px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(100px)" },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  )

  return (
    <DndContext sensors={sensors} modifiers={[restrictToWindowEdges]}>
      <div className={cx(styles.callAssessmentContainer, {
        [styles.open]: showAssessmentView || showCallRecordingView,
      })}>
        {assessmentTransition((style, item) => (
          item && <animated.div style={style} className={cx(styles.stack, {
            [styles.minimized]: playerMinimized,
          })}>
            <div className={styles.callAssessment}>
              <div className={styles.summaryContainer}>
                <img className={styles.icon} src="/images/coaching/trophy.svg" alt="Congratulations" />
                <h4 className={styles.message}>Congratulations! <br /> your overall score is <b>{(2 * report.score).toFixed(1)}</b>/10</h4>
                <div className={styles.details}>
                  <div className={styles.metadata}>
                    <div className={styles.label}>
                      <i className="paperflite-calendar"></i>
                      Call Logged
                    </div>
                    <div className={styles.value}>
                      {dateFormat(report?.startedDate, 'dd mmm yyyy hh:MMTT')}
                    </div>
                  </div>
                  <div className={styles.divider} />
                  <div className={styles.metadata}>
                    <div className={styles.label}>
                      <i className="paperflite-clock"></i>
                      Duration
                    </div>
                    <div className={styles.value}>{FileService.formatDuration(report?.duration, 'long')}</div>
                  </div>
                  <div className={styles.divider} />
                  <div className={cx(styles.metadata, styles.persona)}>
                    <div className={styles.label}>
                      <i className="paperflite-user-settings"></i>
                      Prospect
                    </div>
                    <div className={styles.value}>
                      <Thumbnail className={styles.icon} media={null} defaultImage="/images/assets/default_asset.jpg" />
                      {simulation.simulation?.persona?.name || ""}, {simulation.simulation?.persona?.designation || ""}
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.tabContainer}>
                <Tab.Container defaultActiveKey="0">
                  <Nav className={styles.parametersContainer}>
                    {(report.parameters || []).map((parameter, index) => {
                      return (
                        <Nav.Link key={parameter.uuid} className={styles.parameter} eventKey={index}>
                          <div className={styles.name}>{`${parameter.name}`}</div>
                          <div className={styles.score}>{`${parseFloat((parameter.score).toFixed(2))}`}</div>
                        </Nav.Link>
                      )
                    })}
                    {_.size(report?.factChecks) > 0 && (
                      <Nav.Link
                        className={cx(styles.parameter, styles.factCheckerTab)}
                        eventKey="fact-checker"
                      >
                        <div className={styles.name}>Claim Validation</div>
                        <div className={styles.factChecker}>
                          <span className={styles.count}>
                            {_.size(report.factChecks)}
                          </span>
                          <span className={styles.label}>incorrect claims</span>
                          <i className={cx("fa-solid fa-triangle-exclamation", styles.icon)} />
                        </div>
                      </Nav.Link>
                    )}
                  </Nav>

                  <Tab.Content className={styles.tabContent}>
                    {(report.parameters || []).map((parameter, index) => {
                      return (
                        <Tab.Pane eventKey={index} key={parameter.uuid}>
                          <CallAssessmentParameterView simulation={simulation} parameter={parameter} />
                        </Tab.Pane>
                      )
                    })}
                    {_.size(report?.factChecks) > 0 && (
                      <Tab.Pane eventKey="fact-checker">
                        <CallAssessmentFactInsights
                          simulation={simulation}
                          factChecks={report.factChecks}
                        />
                      </Tab.Pane>
                    )}
                  </Tab.Content>
                </Tab.Container>
              </div>
            </div>
          </animated.div>
        ))}

        {callRecordingTransition((style, item) => {
          const { transform, ...rest } = style;
          return (
            item && <animated.div style={{
              ...rest,
              transform: transform.to((t) => (t ? `translateX(${t}%)` : 'none')),
            }} className={cx(styles.stack, {
              [styles.minimized]: playerMinimized,
            })}>
              <CallRecordingView simulation={simulation} minimized={playerMinimized} />
            </animated.div>
          )
        })}
      </div>
    </DndContext>
  )
}

interface CallAssessmentFactInsightsProps {
  simulation: LearningRecordSimulation;
  factChecks: FactCheckEvaluation[];
}

const CallAssessmentFactInsights: React.FC<CallAssessmentFactInsightsProps> = ({
  simulation,
  factChecks
}) => {
  return (
    <div className={styles.callAssessmentParameterContainer}>
      <p className={styles.message}>
        here&apos;s the list of claims validated
      </p>

      <div className={styles.parameterTabContainer}>
        <Tab.Container defaultActiveKey="0">

          <Nav className={styles.tabs}>
            {factChecks.map((item, index) => (
              <Nav.Link
                key={index}
                className={styles.tab}
                eventKey={index}
              >
                <span>Claim {index + 1}</span>
              </Nav.Link>
            ))}
          </Nav>

          <Tab.Content className={styles.tabContent}>
            {factChecks.map((item, index) => {

              return (
                <Tab.Pane
                  className={styles.parameterContainer}
                  eventKey={index}
                  key={index}
                >
                  <div className={styles.parameter}>
                    <div className={styles.label}>Rep Statement <span className={styles.timestamp}>{item.timestamp}</span></div>
                    <div className={styles.value}>
                      {item?.claim}
                    </div>
                  </div>

                  {item?.feedback && (
                    <div className={styles.parameter}>
                      <div className={styles.label}>Knowledge Reference</div>
                      <div className={styles.value}>
                        {item?.feedback}
                      </div>
                    </div>
                  )}

                  {/* {evidence?.answer && (
                    <div className={styles.parameter}>
                      <div className={styles.label}>Verified answer</div>

                      <div className={styles.value}>
                        {evidence.answer}
                      </div>

                      {evidence.citations?.length > 0 && (
                        <div className={styles.citations}>
                          {evidence.citations.map((citation, i) => (
                            <div key={i} className={styles.citation}>
                              <span className={styles.citationIndex}>
                                {i + 1}
                              </span>
                              <span className={styles.citationText}>
                                {citation}
                              </span>
                              <i
                                className={cx(
                                  "paperflite-arrow-up",
                                  styles.citationArrow
                                )}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )} */}
                </Tab.Pane>
              );
            })}
          </Tab.Content>
        </Tab.Container>
      </div>
    </div>
  );
};

interface CallAssessmentParameterViewProps {
  simulation: LearningRecordSimulation;
  parameter: SimulationParameterEvaluation;
}

const CallAssessmentParameterView: React.FC<CallAssessmentParameterViewProps> = ({ simulation, parameter }) => {
  return (
    <div className={styles.callAssessmentParameterContainer}>
      <p className={styles.message}>here&apos;s the list of evaluated parameters for you,</p>
      <div className={styles.parameterTabContainer}>
        <Tab.Container defaultActiveKey="0">
          <Nav className={styles.tabs}>
            {parameter.parameters.map((parameter, index) => {
              return (
                <Nav.Link key={parameter.uuid} className={styles.tab} eventKey={index}>
                  <span>{parameter.name} ({parameter.score})</span>
                </Nav.Link>
              )
            })}
          </Nav>
          <Tab.Content className={styles.tabContent}>
            {parameter.parameters.map((subParameter, index) => {
							const showEmptyMessage =
                _.isEmpty(subParameter.scenarios) &&
                _.isEmpty(subParameter.positives) &&
                _.isEmpty(subParameter.suggestions) &&
                _.isEmpty(subParameter.missedOpportunities) &&
                (!subParameter.score || subParameter.score === 0);

              if(showEmptyMessage) {
                return (
                  <Tab.Pane
                    className={styles.parameterContainer}
                    eventKey={index}
                    key={subParameter.uuid}
                  >
                    <div className={styles.noData}>
                      <div className={styles.text}>No scenario found</div>
                      <span className={styles.subText}>
                        There were no relevant scenarios in the call to evaluate
                        this parameter.
                      </span>
                    </div>
                  </Tab.Pane>
                );
              }

              return (
                <Tab.Pane
                  className={styles.parameterContainer}
                  eventKey={index}
                  key={subParameter.uuid}
                >
                  <div className={styles.parameter}>
                    <div className={styles.label}>Description</div>
                    <div className={styles.value}>
                      {subParameter.description}
                    </div>
                  </div>
                  <div className={styles.parameter}>
                    <div className={styles.label}>Score</div>
                    <div className={styles.value}>{subParameter.score}</div>
                  </div>
                  {_.size(subParameter.scenarios) > 0 && (
                    <div className={styles.parameter}>
                      <div className={styles.label}>Scenario</div>
                      <div className={styles.value}>
                        <ul className={styles.content}>
                          {_.map(subParameter.scenarios, (scenario, index) => (
                            <li key={index}>
                              <p>{scenario.description}</p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {subParameter.positives && (
                    <div className={styles.parameter}>
                      <div className={styles.label}>Positives</div>
                      <div className={styles.value}>
                        {subParameter.positives}
                      </div>
                    </div>
                  )}

                  <div className={styles.parameter}>
                    <div className={styles.label}>Suggestions</div>
                    <div className={styles.value}>
                      {subParameter.suggestions}
                    </div>
                  </div>

                  {_.size(subParameter.missedOpportunities) > 0 && (
                    <div className={styles.parameter}>
                      <div className={styles.label}>Missed opportunities</div>
                      <div className={styles.value}>
                        <ul className={styles.content}>
                          {_.map(
                            subParameter.missedOpportunities,
                            (item, index) => (
                              <li key={index}>
                                <p className={styles.example}>{item}</p>
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    </div>
                  )}
                </Tab.Pane>
              );
            })}
          </Tab.Content>
        </Tab.Container>
      </div>
    </div>
  );
};

interface TimerProps {
  duration: number;
  disabled: boolean;
  onFinish?: () => void;
}

const Timer: React.FC<TimerProps> = ({ duration, disabled, onFinish }) => {
  const [countDown, setCountDown] = useState(duration);

  useEffect(() => {
    let interval: any = null;
    if (!disabled) {
      interval = setInterval(() => {
        setCountDown(time => Math.max(time - 1, 0));
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    };
  }, [disabled]);

  useEffect(() => {
    setCountDown(duration);
  }, [duration]);

  useEffect(() => {
    if (countDown <= 0) {
      if (onFinish) {
        onFinish();
      }
    }
  }, [countDown]);

  const display = useMemo(() => {
    const seconds = Math.floor((countDown) % 60);
    const minutes = Math.floor((countDown / (60)) % 60);
    return `${_.padStart(String(minutes), 2, '0')}:${_.padStart(String(seconds), 2, '0')}`;
  }, [countDown]);

  return (
    <div className={styles.timerValue}>
      {display}
    </div>
  );
}

//Completed call screen
interface CallRecordingViewProps {
  simulation: LearningRecordSimulation;
  minimized: boolean;
}

const CallRecordingView: React.FC<CallRecordingViewProps> = ({ simulation, minimized }) => {
  const simulationContext = React.useContext(SimulationContext);
  const [position, setPosition] = useState({ top: 0, left: 0, bottom: 0, right: 20 });

  const { attributes, listeners, setNodeRef, transform, node } = useDraggable({
    id: 'call-recording-player',
    disabled: !minimized,
  });

  useDndMonitor({
    onDragEnd(event) {
      const { delta } = event;
      if (delta) {
        
        setPosition((prev) => {
          const top = Math.max(prev.top + delta.y, 0);
          const left = Math.max(prev.left + delta.x, 0);

          return {
            ...prev,
            top: top,
            left: left,
          }
        });
      }
    },
  });

  const positionStyle = minimized ? {
    top: position.top,
    left: position.left,
    transform: CSS.Transform.toString(transform) || undefined,
  } : undefined;

  useEffect(() => {
    if (simulation.id) {
      simulationContext.fetchTranscripts?.();
    }
  }, [simulation.id]);

  useEffect(() => {
    if (minimized && node.current) {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const bodyElement = document.querySelector(`.${styles.body}`);
      const bodyWidth = bodyElement?.clientWidth || viewportWidth;
      const bodyTop = bodyElement?.getBoundingClientRect().top || 0;

      const nodeWidth = node.current.offsetWidth || 400; // Default width if not available
      const nodeHeight = node.current.offsetHeight || 300; // Default height if not available

      console.log("Node dimensions:", nodeWidth, nodeHeight);
      console.log("Viewport dimensions:", viewportWidth, viewportHeight);
      console.log("Body dimensions:", bodyWidth, bodyTop);

      setPosition({
        top: viewportHeight - nodeHeight - 40, // 20px from the bottom
        left: viewportWidth - nodeWidth - 40, // 20px from the right
        bottom: 0,
        right: 20
      });
    }
  }, [minimized]);

  return (
    <div className={cx(styles.callContainer, styles.callRecording, {
      [styles.minimized]: minimized,
    })} ref={setNodeRef} style={positionStyle} {...listeners} {...attributes}>
      <div className={styles.left}>
        <div className={styles.details}>
          <div className={styles.participants}>
            <ProspectParticipantView persona={simulation.simulation!.persona!} loading={false} />
            <UserParticipantView />
          </div>
          <div className={styles.name}>{simulation.simulation.name || ""}</div>
        </div>
        <div className={styles.callRecording}>
          <CallRecordingPlayer simulation={simulation} minimized={minimized} />
        </div>
      </div>
      <div className={styles.right}>
        <CallTranscripts simulation={simulation} />
      </div>
    </div>
  )
}

interface CallTranscriptsProps {
  simulation: LearningRecordSimulation;
}

const CallTranscripts: React.FC<CallTranscriptsProps> = ({ simulation }) => {
  const simulationContext = React.useContext(SimulationContext);
  const transcripts = simulationContext.transcripts || [];
  const fetchingTranscripts = simulationContext.fetchingTranscripts || false;

  const userProfile = useSelector((state: any) => {
    const profile = _.get(state, "users.me");
    return {
      name: getFullName(profile),
      icon: _.get(profile, "icon"),
    }
  }, _.isEqual);

  const personaProfile = useMemo(() => {
    const persona = simulation.simulation?.persona;
    return {
      name: persona?.name || "",
      icon: persona?.icon,
    }
  }, [simulation]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    const paddedMins = String(mins).padStart(2, '0');
    const paddedSecs = String(secs).padStart(2, '0');

    if (hrs === 0) {
      return `${paddedMins}:${paddedSecs}`;
    } else {
      const paddedHrs = String(hrs);
      return `${paddedHrs}:${paddedMins}:${paddedSecs}`;
    }
  }

  if (fetchingTranscripts && _.size(transcripts) === 0) {
    return (
      <div className={styles.transcripts}>
        {_.times(4).map((_, index) => (
          <TranscriptRowPlaceholder key={index} />
        ))}
      </div>
    );
  }

  return (
    <div className={styles.transcripts}>
      {(transcripts || []).filter(t => t.message).map((transcript, index) => {
        const role = transcript.role;
        const speaker = role === "user" ? userProfile : personaProfile;
        return (
          <TranscriptRow
            key={index}
            speaker={speaker}
            text={transcript.message}
            time={transcript.secondsFromStart > 0 && FileService.formatTime(transcript.secondsFromStart)}
          />
        )
      })}
    </div>
  );
}